/**
 * Sentry initialization for Onboardix ERP (React SPA).
 * Must be imported BEFORE React renders — see main.jsx.
 *
 * DSN is injected via Vite env: VITE_SENTRY_DSN
 * Set it in Railway Variables (available at build time).
 */
import * as Sentry from '@sentry/react'
import { shouldDropSentryEvent } from './utils/abortError'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || ''

if (SENTRY_DSN) {
    Sentry.init({
        dsn: SENTRY_DSN,
        environment: import.meta.env.MODE, // 'production' | 'development'
        release: `onboardix-erp@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,

        // Performance: sample 20% of transactions
        tracesSampleRate: 0.2,

        // Session Replay — disabled by default to save quota
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,

        // Don't send PII (GDPR)
        sendDefaultPii: false,

        // Filter out noisy/expected errors
        beforeSend(event, hint) {
            const error = hint?.originalException
            // Отменённый запрос (unmount / новый запрос / таймаут) — не ошибка.
            if (shouldDropSentryEvent(event, hint)) return null
            // Offline/timeout errors are an expected flow — keyed off the
            // structured `isOffline` flag (core.js), since the user-facing text
            // is now calm and no longer contains "Сервер недоступен" in prod.
            if (error?.isOffline) {
                return null
            }
            if (typeof error === 'string') {
                // Ignore offline/auth errors (expected flow)
                if (error.includes('Сервер недоступен') || error.includes('Сессия истекла')) {
                    return null
                }
            }
            if (error?.message) {
                if (error.message.includes('Сервер недоступен') || error.message.includes('Сессия истекла')) {
                    return null
                }
                // Ignore ResizeObserver (browser noise)
                if (error.message.includes('ResizeObserver')) {
                    return null
                }
            }
            return event
        },

        // Integrations
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.breadcrumbsIntegration({
                console: false, // Don't capture console.log as breadcrumbs
                fetch: true,
                xhr: true,
            }),
        ],
    })

    console.info('[Sentry] ✓ Initialized (frontend)')
} else {
    console.info('[Sentry] ⚠ Disabled (VITE_SENTRY_DSN not set)')
}

/**
 * Set user context after login.
 * Call this from AuthContext after successful authentication.
 */
export function setSentryUser(user) {
    if (!SENTRY_DSN || !user) return
    Sentry.setUser({
        id: user.id,
        username: user.login,
        // Don't send email (GDPR)
    })
    Sentry.setTag('user.role', user.role || 'unknown')
}

/**
 * Clear user context on logout.
 */
export function clearSentryUser() {
    if (!SENTRY_DSN) return
    Sentry.setUser(null)
}

export { Sentry }
